import React, { useEffect, useState } from 'react';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import { translate } from '../../../services/translate';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { CitizenParamList } from '../../../typings/Params';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import { citizenService } from '../../../services/central-api/citizen';
import MyInfinityDataTable from '../../../components/MyInfinityDataTable';
import PlateImage from '../../../components/lpr/PlateImage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MyAppText from '../../../components/MyAppText';
import moment from 'moment';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import CitizenDropdown from '../../../components/lpr/CitizenDropdown';

export default function CitizenLprEvents({ navigation }: { navigation: Navigation; }) {
    const { styles } = useStyles(stylesheet);
    const { user: authenticatedUser } = useAuth();
    const route = useRoute<RouteProp<CitizenParamList, 'CitizenLprEvents'>>();

    const [filters, setFilters] = useFilters<CitizenParamList['CitizenLprEvents']>({
        ...route.params,
    });
    const [selectedCitizenIds, setSelectedCitizenIds] = useState<number[]>(filters.citizenIds ?? []);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['citizensTable', filters],
        queryFn: () => citizenService.getCitizensLprDetectionsInterval(filters!),
        enabled: !!authenticatedUser,
        refetchInterval: (data) => {
            if (!isFocused) {
                return false;
            }
            if (!data?.refreshInterval) {
                return false;
            }

            return data.refreshInterval;
        },
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data } = queryResult;

    useEffect(() => {
        setFilters({
            ...filters,
            citizenIds: selectedCitizenIds.length > 0 ? selectedCitizenIds : undefined,
        });

        navigation.setParams({
            ...filters,
            citizenIds: selectedCitizenIds.length > 0 ? selectedCitizenIds : undefined,
        });
    }, [selectedCitizenIds]);

    return (<Container>
        <Header title={translate('citizenLprEvents')} />
        <Card>
            <DataTableHeader
                title={translate('registeredDetections')}
                zIndex={2}
                filterInput={<CitizenDropdown
                    setValue={setSelectedCitizenIds}
                    value={selectedCitizenIds}
                    zIndex={2}
                    showLabel={false}
                />}
            />
            <DataTableContainer
                dataTable={<MyInfinityDataTable
                    progressPending={isLoading}
                    data={data?.responseList ?? []}
                    onRowPress={(row) => {
                        navigation.navigate('CitizenLprEventsDetails', { id: row.id });
                    }}
                    columns={[{
                        name: translate('capturedImage'),
                        cell: row =>
                            <View style={{ paddingVertical: 14 }}>
                                <PlateImage
                                    src={row.imageUrl}
                                    platePosition={row.platePosition}
                                />
                            </View>,
                        flex: 1,
                    }, {
                        name: translate('plate'),
                        selector: row => row.plate,
                        flex: 1,
                    }, {
                        name: translate('description'),
                        selector: row => row.description,
                        flex: 1,
                    }, {
                        name: translate('readingCapturedBy'),
                        selector: row => row.citizen.name,
                        flex: 1,
                    }, {
                        name: translate('location'),
                        flex: 1,
                        cell: row => <View style={{ flexBasis: '20%', rowGap: 10, paddingVertical: 8 }}>
                            <View style={styles.locationView} >
                                <FontAwesomeIcon icon={faCalendar as IconDefinition} fontSize={13} style={styles.locationIcon} />
                                <MyAppText style={styles.locationText}>
                                    {moment(new Date(row.time)).format('DD/MM/YYYY HH:mm:ss')}
                                </MyAppText>
                            </View>
                        </View>
                    }]}
                    loadMore={(() => { })}
                />}
            />
        </Card>
    </Container>);
}

const stylesheet = createStyleSheet(theme => ({
    headerCell: {
        fontSize: 14,
        color: theme.colors.labelColor,
    },
    locationView: {
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: 5
    },
    locationIcon: {
        color: theme.colors.textColor,
    },
    locationText: {
        color: theme.colors.disabledText,
        fontWeight: '500',
    }
}));
